/**
 * MonitorStatsCard - Statistics card component for monitor metrics
 *
 * Displays comprehensive statistics for a directory monitor including:
 * - Files detected/ingested/failed counts
 * - Success rate percentage
 * - Average scan duration
 * - Total data size processed
 */

import React from 'react';
import { FileCheck, AlertCircle, Clock, HardDrive, TrendingUp, Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import type { MonitorStats } from '@/features/monitors/types/monitor.types';
import { formatDuration } from '@/utils/formatDuration';

interface MonitorStatsCardProps {
  /**
   * Monitor statistics data
   */
  stats: MonitorStats | null | undefined;
  /**
   * Whether data is loading
   */
  isLoading?: boolean;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Format number with thousands separators
 */
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format file size from bytes to human-readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Calculate success rate percentage
 */
const calculateSuccessRate = (stats: MonitorStats): number => {
  const total = stats.files_detected;
  if (total === 0) return 0;
  const successful = stats.files_ingested;
  return Math.round((successful / total) * 100);
};

/**
 * StatItem - Individual statistic display component
 */
interface StatItemProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color?: string;
}

const StatItem: React.FC<StatItemProps> = ({ icon, label, value, color = 'text-foreground' }) => (
  <div className="flex items-start space-x-3">
    <div className={`mt-1 ${color}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold truncate">{value}</p>
    </div>
  </div>
);

/**
 * MonitorStatsCard Component
 *
 * Displays comprehensive statistics for a directory monitor.
 *
 * @example
 * ```tsx
 * <MonitorStatsCard stats={monitorStats} />
 * <MonitorStatsCard stats={null} isLoading={true} />
 * ```
 */
export const MonitorStatsCard: React.FC<MonitorStatsCardProps> = ({
  stats,
  isLoading = false,
  className
}) => {
  // Loading state
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Monitor Statistics</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!stats) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Monitor Statistics</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No statistics available yet. Monitor needs to run at least one scan.
          </div>
        </CardContent>
      </Card>
    );
  }

  const successRate = calculateSuccessRate(stats);
  const successRateColor = successRate >= 90
    ? 'text-success'
    : successRate >= 70
    ? 'text-warning'
    : 'text-destructive';

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Activity className="h-5 w-5" />
          <span>Monitor Statistics</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Files Detected */}
          <StatItem
            icon={<FileCheck className="h-5 w-5" />}
            label="Files Detected"
            value={formatNumber(stats.files_detected)}
            color="text-info"
          />

          {/* Files Ingested */}
          <StatItem
            icon={<FileCheck className="h-5 w-5" />}
            label="Files Ingested"
            value={formatNumber(stats.files_ingested)}
            color="text-success"
          />

          {/* Files Failed */}
          <StatItem
            icon={<AlertCircle className="h-5 w-5" />}
            label="Files Failed"
            value={formatNumber(stats.files_failed)}
            color="text-destructive"
          />

          {/* Success Rate */}
          <StatItem
            icon={<TrendingUp className="h-5 w-5" />}
            label="Success Rate"
            value={`${successRate}%`}
            color={successRateColor}
          />

          {/* Average Scan Duration */}
          <StatItem
            icon={<Clock className="h-5 w-5" />}
            label="Avg Scan Duration"
            value={formatDuration(stats.average_scan_duration)}
            color="text-violet"
          />

          {/* Total Data Size */}
          <StatItem
            icon={<HardDrive className="h-5 w-5" />}
            label="Total Data Processed"
            value={formatFileSize(stats.total_data_size)}
            color="text-info"
          />

          {/* Files Pending (if available) */}
          {stats.files_pending !== undefined && (
            <StatItem
              icon={<Clock className="h-5 w-5" />}
              label="Files Pending"
              value={formatNumber(stats.files_pending)}
              color="text-alert"
            />
          )}

          {/* Files Skipped (if available) */}
          {stats.files_skipped !== undefined && (
            <StatItem
              icon={<FileCheck className="h-5 w-5" />}
              label="Files Skipped"
              value={formatNumber(stats.files_skipped)}
              color="text-muted-foreground"
            />
          )}

          {/* Recent Activity (if available) */}
          {stats.scans_last_24h !== undefined && (
            <StatItem
              icon={<Activity className="h-5 w-5" />}
              label="Scans (24h)"
              value={formatNumber(stats.scans_last_24h)}
              color="text-teal"
            />
          )}
        </div>

        {/* Last Successful Scan */}
        {stats.last_successful_scan && (
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-muted-foreground">
              Last successful scan:{' '}
              <span className="font-medium text-foreground">
                {new Date(stats.last_successful_scan).toLocaleString()}
              </span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
